<template>
  <div class="flex-grow-1 w-full">
    <div
      id="map-wrapper"
      class="map-wrapper flex-grow-1 font-bold"
      :class="{
        invisible: !mapHasFinishedLoading,
      }"></div>
    <ErrorMessage name="general.canton" class="error-message ml-2" />
    <ErrorMessage name="canton" class="error-message ml-2" />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { MapBridge, type RegionData, type MapData, type TooltipLabels } from '@/services/map/map-bridge'
import ErrorMessage from '@/components/shared/ErrorMessage.vue'
import type { RegionCH, ProjectFormData } from './types'
import { deepClone } from '@/helpers/objects_arrays'
import type { PropType } from 'vue'
import i18n from '@/i18n'
import { Canton } from '@/schemas/ch/enums'
import { getRegionForCanton } from '@/helpers/region'

const modelValue = defineModel({ type: Object as PropType<ProjectFormData> })
const style = getComputedStyle(document.body)
const hoverColor = style.getPropertyValue('--color-primary-light') || '#9B9B9B'
const activeColor = style.getPropertyValue('--color-primary-default') || '#6E6E6E'

let mapHasFinishedLoading = ref(false)
let mapBridge: MapBridge | null = null
const cantonIds = new Set<string>(Object.values(Canton))

onMounted(() => {
  mapBridge = new MapBridge(
    hoverColor,
    activeColor,
    '#map-wrapper',
    modelValue?.value?.canton || 'init',
    clickHandler,
    mapHasLoaded,
    resolveTooltipLabels
  )
  mapBridge.createMap()
})
onUnmounted(() => {
  // dispose chart
  mapBridge?.dispose()
  mapBridge = null
})

const mapHasLoaded = function (): boolean {
  mapHasFinishedLoading.value = true
  return true
}

function resolveTooltipLabels(data: MapData): TooltipLabels {
  if (!cantonIds.has(data.id)) {
    return { name: data.name, region: data.region }
  }

  const cantonKey = `project.general.geography.cantons.${data.id}`
  const regionCode = getRegionForCanton(data.id as Canton)
  const regionKey = `project.general.geography.regions.${regionCode}`

  const name = translateOrFallback(cantonKey, data.name)
  const region = translateOrFallback(regionKey, data.region)
  return { name, region }
}

function translateOrFallback(key: string, fallback: string): string {
  const translated = i18n.global.t(key)
  if (typeof translated !== 'string' || translated === key) {
    return fallback
  }
  return translated
}

const clickHandler = async function (regionData: RegionData) {
  if (regionData && modelValue?.value) {
    const modelValueClone = deepClone(modelValue.value)
    modelValueClone.canton = regionData.data.id as RegionCH
    modelValue.value = modelValueClone
  }
}
</script>

<style lang="postcss" scoped>
.map-wrapper {
  @apply h-[280px] text-black md:h-[500px] lg:p-6;
  &.has-overlay {
    &:after {
      content: ' ';
      @apply absolute inset-0 block h-full w-full;
      background: rgba(255, 255, 255, 0.05);
    }
  }
}
</style>
